"use client"

import { Check } from "lucide-react"
import { formatImageURL } from "@/utils/util"
import { Label } from "@/components/ui/label"

interface ImagePickerProps {
  label?: string
  images: string[]
  // A single URL, or an array of them when `multiple` is on.
  value?: string | string[]
  onChange: (_value: string | string[]) => void
  multiple?: boolean
  required?: boolean
  color?: string
}

export default function ImagePicker({ label, images, value, onChange, multiple = false, required = false, color }: ImagePickerProps) {
  if (images.length === 0) return null

  const selected = Array.isArray(value) ? value : value ? [value] : []
  const accent = color || 'hsl(var(--primary))'

  const toggle = (image: string) => {
    if (!multiple) return onChange(selected[0] === image ? '' : image)
    onChange(selected.includes(image) ? selected.filter(img => img !== image) : [...selected, image])
  }

  return (
    <div className="col-span-full">
      <Label>{label}{required && <span className="text-red-600 ml-1">*</span>}</Label>
      <p className="text-xs text-muted-foreground mt-1">
        {multiple ? 'Дарж сонгоно уу (хэд хэдийг сонгож болно)' : 'Дарж нэг зургийг сонгоно уу'}
      </p>
      <div className="mt-2.5 grid grid-cols-4 sm:grid-cols-5 gap-2">
        {images.map((image) => {
          const isSelected = selected.includes(image)
          return (
            <button
              key={image}
              type="button"
              onClick={() => toggle(image)}
              aria-pressed={isSelected}
              title={isSelected ? 'Сонгосон' : 'Сонгох'}
              className={`aspect-square relative rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${isSelected ? 'shadow-sm' : 'border-border opacity-80 hover:opacity-100 hover:border-muted-foreground/50'}`}
              style={isSelected ? { borderColor: accent, boxShadow: `0 0 0 2px ${accent}33` } : undefined}
            >
              <img src={formatImageURL(image)} alt="" className="w-full h-full object-cover bg-muted" />
              {/* Dim wash under the badge so the picked state reads on white product shots. */}
              {isSelected && <span className="absolute inset-0 bg-black/15" />}
              {/* Neutral dark badge, not the brand accent — a pale accent would vanish here. */}
              <span className={`absolute top-1 right-1 h-5 w-5 rounded-full grid place-items-center ring-1 ring-white/80 shadow-sm transition-colors ${isSelected ? 'bg-black/75 text-white' : 'bg-black/25 text-transparent'}`}>
                <Check size={12} strokeWidth={3} />
              </span>
            </button>
          )
        })}
      </div>
      {selected.length > 0 && (
        <p className="text-xs text-muted-foreground mt-1.5">{multiple ? `${selected.length} зураг сонгосон` : 'Сонгосон'}</p>
      )}
    </div>
  )
}
